/**
 * StreamX `/streamx/replays` — Cinematic Replay Archive.
 * StreamX Sprint 3c rebuild.
 * ------------------------------------------------------------------
 * Doctrine:
 *   • Obsidian #0A0A0A · glass-card archive (white/[0.02] + white/[0.08])
 *   • 4-column responsive grid with generous breathing room
 *   • Date-grouped sections: This Week · Last Week · This Month · Earlier
 *   • Sport filter chips + search input
 *   • Cinematic empty state ("Your archive begins here")
 *   • Motion: 700ms hover lift · shimmer cards on load · zero gaming neon
 *
 * Behaviour preserved verbatim:
 *   • GET /livex/me/replays?limit=50  (authenticated)
 *   • POST /livex/replays/:id/share   (copy-to-clipboard share)
 *   • DELETE /livex/replays/:id       (with confirm)
 *   • ReplayExpiryPill + ReplayPreserveBanner integrations
 */
import { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import api from '@/lib/api';
import {
  ArrowLeft, Film, Share2, Trash2, AlertTriangle, Eye, Clock,
  Sparkles, Radio, Check, Calendar, Search, Play, X,
  Download, RefreshCw,
} from 'lucide-react';
import { ReplayExpiryPill, ReplayPreserveBanner } from '@/components/livex/LivexReplayExpiryBanner';
import { STREAMX_BRAND } from '@/lib/brand';

// ── Helpers ──────────────────────────────────────────────────────────

function fmtDuration(sec) {
  const s = Number(sec || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function fmtViews(n) {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function daysAgo(iso) {
  try {
    const d = new Date(iso);
    return Math.floor((Date.now() - d.getTime()) / 86400000);
  } catch { return 999; }
}

function bucketLabel(days) {
  if (days <= 7) return 'This Week';
  if (days <= 14) return 'Last Week';
  if (days <= 30) return 'This Month';
  return 'Earlier';
}

const BUCKET_ORDER = ['This Week', 'Last Week', 'This Month', 'Earlier'];

const SPORT_TINT = {
  football: { bg: 'rgba(59,130,246,0.10)', text: '#93c5fd', border: 'rgba(59,130,246,0.30)' },
  cricket: { bg: 'rgba(16,185,129,0.10)', text: '#6ee7b7', border: 'rgba(16,185,129,0.30)' },
  basketball: { bg: 'rgba(249,115,22,0.10)', text: '#fdba74', border: 'rgba(249,115,22,0.30)' },
  tennis: { bg: 'rgba(168,85,247,0.10)', text: '#d8b4fe', border: 'rgba(168,85,247,0.30)' },
  hockey: { bg: 'rgba(239,68,68,0.10)', text: '#fca5a5', border: 'rgba(239,68,68,0.30)' },
  badminton: { bg: 'rgba(6,182,212,0.10)', text: '#67e8f9', border: 'rgba(6,182,212,0.30)' },
  default: { bg: 'rgba(255,255,255,0.05)', text: '#e2e8f0', border: 'rgba(255,255,255,0.12)' },
};
const sportTint = (s) => SPORT_TINT[(s || '').toLowerCase()] || SPORT_TINT.default;

// ── Primitives ───────────────────────────────────────────────────────

function Anim({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 14 }}
      animate={v ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ShimmerCard() {
  return (
    <div
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <div className="aspect-video relative">
        <motion.div
          className="absolute inset-0"
          animate={{ x: ['-100%', '100%'] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: 'linear' }}
          style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)' }}
        />
      </div>
      <div className="p-4 space-y-2">
        <div className="h-3 rounded bg-white/[0.04]" />
        <div className="h-3 rounded bg-white/[0.04] w-2/3" />
      </div>
    </div>
  );
}

// ── Replay card (grid form) ──────────────────────────────────────────

function ReplayCard({ r, onShare, onDelete, onRetry, busy }) {
  const tint = sportTint(r.sport);
  const isPurged = r.status === 'purged';
  const isReady = r.status === 'ready';

  return (
    <div
      data-testid={`streamx-replay-card-${r.id}`}
      className="group relative flex flex-col rounded-2xl overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 hover:shadow-[0_24px_64px_rgba(255,59,48,0.14)]"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(24px)',
      }}
    >
      {/* Thumbnail */}
      <Link
        to={`/streamx/replays/${r.id}`}
        data-testid={`streamx-replay-card-${r.id}-open`}
        className="relative aspect-video overflow-hidden"
        style={{
          background: `radial-gradient(50% 50% at 45% 45%, ${tint.text}22, #0A0A0A 75%), linear-gradient(135deg, #14080A 0%, #0A0A0A 100%)`,
        }}
      >
        <div className="absolute inset-0 grid place-items-center">
          <motion.div
            animate={{ scale: [1, 1.04, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            className="h-14 w-14 rounded-full grid place-items-center transition-transform duration-700 group-hover:scale-110"
            style={{
              background: 'linear-gradient(135deg, rgba(255,59,48,0.92), rgba(153,0,0,0.92))',
              boxShadow: '0 14px 36px rgba(255,59,48,0.32)',
            }}
          >
            <Play className="h-5 w-5 text-white ml-0.5" fill="currentColor" />
          </motion.div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/80 to-transparent" />
        {isPurged && (
          <div className="absolute inset-0 grid place-items-center bg-black/60">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">Retention expired</span>
          </div>
        )}
        {r.status === 'processing' && (
          <span
            className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.18em]"
            style={{ background: 'rgba(251,191,36,0.18)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.45)' }}
            data-testid={`streamx-replay-card-${r.id}-pill-processing`}
          >
            Processing
          </span>
        )}
        {r.status === 'failed' && (
          <span
            className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.18em]"
            style={{ background: 'rgba(244,63,94,0.18)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.45)' }}
            title={r.status_note || 'Recording could not be generated'}
            data-testid={`streamx-replay-card-${r.id}-pill-failed`}
          >
            Recording Failed
          </span>
        )}
        {isReady && (
          <span
            className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.18em]"
            style={{ background: 'rgba(16,185,129,0.18)', color: '#6ee7b7', border: '1px solid rgba(16,185,129,0.45)' }}
            data-testid={`streamx-replay-card-${r.id}-pill-ready`}
          >
            Ready
          </span>
        )}
        <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tabular-nums"
          style={{
            background: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(255,255,255,0.10)',
            color: 'white',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Clock className="h-2.5 w-2.5" /> {fmtDuration(r.duration_seconds)}
        </div>
      </Link>

      {/* Body */}
      <div className="flex-1 p-4 sm:p-5 flex flex-col">
        <Link
          to={`/streamx/replays/${r.id}`}
          className="block"
          data-testid={`streamx-replay-card-${r.id}-title-link`}
        >
          <h3 className="text-base font-semibold tracking-tight text-white leading-snug line-clamp-2 group-hover:text-[#FF6B5C] transition-colors">
            {r.title}
          </h3>
        </Link>
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {r.sport && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider capitalize"
              style={{ background: tint.bg, color: tint.text, border: `1px solid ${tint.border}` }}
            >
              {r.sport}
            </span>
          )}
          <ReplayExpiryPill meta={r.expiry_meta} />
        </div>
        <div className="mt-3 flex items-center gap-3 text-[11px] font-medium text-white/45 tabular-nums">
          <span className="inline-flex items-center gap-1"><Eye className="h-2.5 w-2.5" /> {fmtViews(r.view_count)}</span>
          <span className="inline-flex items-center gap-1"><Share2 className="h-2.5 w-2.5" /> {fmtViews(r.share_count)}</span>
          <span className="inline-flex items-center gap-1"><Sparkles className="h-2.5 w-2.5" /> {r.highlights_count || 0}</span>
        </div>

        <ReplayPreserveBanner meta={r.expiry_meta} replayTitle={r.title} />

        <div className="mt-4 flex items-center gap-1.5 pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          {isPurged ? (
            <div className="flex-1 text-center text-[11px] font-semibold text-white/35 uppercase tracking-wider py-2">
              Archived · cannot share
            </div>
          ) : r.status === 'failed' ? (
            <>
              {/* Phase 140A-replay-fix — Recovery action for failed recordings. */}
              <button
                onClick={() => onRetry && onRetry(r)}
                disabled={busy || !onRetry}
                data-testid={`streamx-replay-card-${r.id}-retry`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold text-amber-200 hover:text-amber-100 transition-all duration-500 disabled:opacity-50"
                style={{ background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.35)' }}
                title={r.status_note || 'Retry recording'}
              >
                <RefreshCw className="h-3 w-3" /> Retry
              </button>
              <button
                onClick={() => onDelete(r)}
                disabled={busy}
                data-testid={`streamx-replay-card-${r.id}-delete`}
                aria-label={`Delete ${r.title}`}
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-white/45 hover:text-rose-300 transition-colors"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <>
              {/* Phase 140A-replay-fix — Download MP4 surfaced as a primary action
                  for ready replays. Falls back gracefully when mp4_url isn't set
                  (e.g. still processing). */}
              {isReady && r.mp4_url && (
                <a
                  href={r.mp4_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  data-testid={`streamx-replay-card-${r.id}-download`}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold text-emerald-200 hover:text-emerald-100 transition-all duration-500"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.35)' }}
                  title="Download MP4 to your device"
                >
                  <Download className="h-3 w-3" /> MP4
                </a>
              )}
              <button
                onClick={() => onShare(r)}
                disabled={busy}
                data-testid={`streamx-replay-card-${r.id}-share`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold text-white/80 hover:text-white transition-all duration-500 disabled:opacity-50"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Share2 className="h-3 w-3" /> Share
              </button>
              <button
                onClick={() => onDelete(r)}
                disabled={busy}
                data-testid={`streamx-replay-card-${r.id}-delete`}
                aria-label={`Delete ${r.title}`}
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-white/45 hover:text-rose-300 transition-colors"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Filter + search bar ──────────────────────────────────────────────

function ArchiveControls({ search, setSearch, sports, activeSport, setActiveSport, total }) {
  return (
    <div className="space-y-4" data-testid="streamx-replays-controls">
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-center">
        <div
          className="relative rounded-xl overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(24px)',
          }}
        >
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/35" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search the archive…"
            data-testid="streamx-replays-search"
            className="w-full pl-11 pr-10 py-3.5 bg-transparent text-sm text-white placeholder-white/35 focus:outline-none font-medium"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              data-testid="streamx-replays-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-white/45 hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45 tabular-nums">
          {total} {total === 1 ? 'replay' : 'replays'}
        </div>
      </div>

      {sports.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <button
            onClick={() => setActiveSport('all')}
            data-testid="streamx-replays-filter-all"
            className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-500 ${
              activeSport === 'all' ? 'text-white' : 'text-white/55 hover:text-white'
            }`}
            style={{
              background: activeSport === 'all'
                ? 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)'
                : 'rgba(255,255,255,0.03)',
              border: activeSport === 'all'
                ? '1px solid rgba(255,59,48,0.45)'
                : '1px solid rgba(255,255,255,0.08)',
              boxShadow: activeSport === 'all' ? '0 0 20px rgba(255,59,48,0.30)' : 'none',
            }}
          >
            All sports
          </button>
          {sports.map((sp) => {
            const tint = sportTint(sp);
            const sel = activeSport === sp;
            return (
              <button
                key={sp}
                onClick={() => setActiveSport(sp)}
                data-testid={`streamx-replays-filter-${sp.toLowerCase()}`}
                className="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider capitalize transition-all duration-500"
                style={{
                  background: sel ? tint.bg : 'rgba(255,255,255,0.03)',
                  color: sel ? tint.text : 'rgba(255,255,255,0.55)',
                  border: `1px solid ${sel ? tint.border : 'rgba(255,255,255,0.08)'}`,
                }}
              >
                {sp}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────────────────

function ArchiveEmptyState() {
  return (
    <section
      className="relative overflow-hidden rounded-3xl min-h-[55vh] flex items-center justify-center"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
      data-testid="streamx-replays-empty"
    >
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(40% 30% at 30% 30%, rgba(255,59,48,0.16) 0%, transparent 60%),' +
            'radial-gradient(40% 30% at 70% 70%, rgba(153,0,0,0.16) 0%, transparent 60%)',
        }}
      />
      <div className="relative px-6 sm:px-12 py-16 max-w-2xl text-center">
        <Anim>
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl mb-7"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.08)',
              backdropFilter: 'blur(24px)',
            }}
          >
            <Film className="h-7 w-7 text-white/40" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white leading-none">
            Your archive begins here.
          </h2>
          <p className="mt-6 text-base text-white/55 leading-relaxed max-w-lg mx-auto font-medium">
            Every match you broadcast becomes a permanent replay — chapters marked,
            highlights surfaced, and a public link ready to share.
          </p>
          <Link
            to="/streamx/studio"
            data-testid="streamx-replays-empty-cta"
            className="mt-9 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold text-white transition-all duration-500"
            style={{
              background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)',
              boxShadow: '0 12px 32px rgba(255,59,48,0.35)',
            }}
          >
            <Radio className="h-4 w-4" /> Start broadcasting
          </Link>
        </Anim>
      </div>
    </section>
  );
}

function NoResultsBlock({ onClear }) {
  return (
    <div
      className="rounded-2xl p-10 text-center"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
      data-testid="streamx-replays-no-results"
    >
      <Search className="h-7 w-7 text-white/35 mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-white tracking-tight">
        No Archives Found.
      </h3>
      <p className="mt-2 text-sm text-white/55 font-medium">
        Adjust your filters or clear search to explore the library.
      </p>
      <button
        onClick={onClear}
        data-testid="streamx-replays-clear-filters"
        className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-white/85 hover:text-white transition-all"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.10)',
        }}
      >
        Clear filters
      </button>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────

export default function LivexReplays() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [toast, setToast] = useState('');
  const [search, setSearch] = useState('');
  const [activeSport, setActiveSport] = useState('all');

  const load = async () => {
    setError('');
    try {
      const r = await api.get('/livex/me/replays?limit=50');
      setEntries(r.data?.entries || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load replays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  useEffect(() => {
    document.title = `Replay Archive — ${STREAMX_BRAND} by Out-Play`;
  }, []);

  const handleShare = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/livex/replays/${r.id}/share`);
      const shareUrl = `${window.location.origin}/streamx/replays/public/${r.public_slug}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        setToast('Share link copied to clipboard');
      } catch {
        setToast(`Share link: ${shareUrl}`);
      }
      setEntries((es) => es.map((x) => x.id === r.id ? { ...x, share_count: (x.share_count || 0) + 1 } : x));
    } catch {
      setToast('Failed to register share');
    } finally {
      setBusyId('');
      setTimeout(() => setToast(''), 5000);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone — the public share link will stop working.`)) return;
    setBusyId(r.id);
    try {
      await api.delete(`/livex/replays/${r.id}`);
      setEntries((es) => es.map((x) => x.id === r.id ? { ...x, status: 'purged', mp4_url: null } : x));
      setToast('Replay deleted');
    } catch {
      setToast('Failed to delete');
    } finally {
      setBusyId('');
      setTimeout(() => setToast(''), 5000);
    }
  };

  // Phase 140A-replay-fix — Recovery action for replays stuck in `failed`.
  // Hits POST /livex/replays/{id}/retry-processing; backend either re-fires
  // egress (if the source stream is still live) or honestly flips the card
  // to `failed` with a helpful note (if the stream is gone).
  const handleRetry = async (r) => {
    setBusyId(r.id);
    try {
      const { data } = await api.post(`/livex/replays/${r.id}/retry-processing`);
      if (data?.status === 'retrying') {
        setEntries((es) => es.map((x) => x.id === r.id ? { ...x, status: 'processing' } : x));
        setToast('Retrying — refresh in a minute');
      } else if (data?.status === 'ready') {
        setEntries((es) => es.map((x) => x.id === r.id ? { ...x, status: 'ready' } : x));
        setToast('Replay reconciled — ready to watch');
      } else {
        // Backend already marked it failed (stream gone / ended).
        setToast(data?.action === 'stream_ended'
          ? 'Cannot recover — broadcast ended before recording started'
          : 'Cannot recover this replay');
      }
    } catch {
      setToast('Retry failed — please try again');
    } finally {
      setBusyId('');
      setTimeout(() => setToast(''), 5000);
    }
  };

  // Sport facet derivation
  const availableSports = useMemo(() => {
    const set = new Set();
    entries.forEach((e) => { if (e.sport) set.add(String(e.sport).toLowerCase()); });
    return Array.from(set).sort();
  }, [entries]);

  // Filtered + searched
  const filtered = useMemo(() => {
    let out = entries;
    if (activeSport !== 'all') {
      out = out.filter((r) => (r.sport || '').toLowerCase() === activeSport);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((r) => (r.title || '').toLowerCase().includes(q));
    }
    return out;
  }, [entries, activeSport, search]);

  // Date-grouped buckets
  const grouped = useMemo(() => {
    const out = { 'This Week': [], 'Last Week': [], 'This Month': [], 'Earlier': [] };
    filtered.forEach((r) => {
      const lbl = bucketLabel(daysAgo(r.recorded_at));
      out[lbl].push(r);
    });
    return out;
  }, [filtered]);

  const hasAnyFilter = activeSport !== 'all' || search.trim().length > 0;
  const showEmpty = !loading && entries.length === 0;
  const showNoResults = !loading && entries.length > 0 && filtered.length === 0;

  return (
    <div
      className="min-h-screen text-white"
      style={{ background: '#0A0A0A' }}
      data-testid="streamx-replays-page"
    >
      {/* Top bar */}
      <header
        className="sticky top-0 z-40 border-b"
        style={{
          background: 'rgba(0,0,0,0.65)',
          borderColor: 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(24px)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/streamx/dashboard"
            data-testid="streamx-replays-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </Link>
          <Link
            to="/streamx/studio"
            data-testid="streamx-replays-go-live"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all duration-500"
            style={{
              background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)',
              boxShadow: '0 0 18px rgba(255,59,48,0.30)',
            }}
          >
            <Radio className="h-3.5 w-3.5" /> Go live
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(50% 40% at 50% 0%, rgba(255,59,48,0.14) 0%, transparent 60%),' +
              '#0A0A0A',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-10 sm:pb-14">
          <Anim>
            <div className="text-[10px] font-bold tracking-[0.32em] text-white/45 uppercase mb-3">
              {STREAMX_BRAND} · YOUR ARCHIVE
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.02]">
              Every match,{' '}
              <span
                style={{
                  background: 'linear-gradient(120deg, #FF3B30 0%, #FF6B5C 50%, #990000 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                preserved.
              </span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/55 max-w-2xl leading-relaxed font-medium">
              Your broadcast archive — chapters marked, highlights surfaced,
              public share links ready. Search, filter, and revisit every moment.
            </p>
          </Anim>
        </div>
      </section>

      {/* Body */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-20 space-y-8 sm:space-y-10">
        {toast && (
          <div
            className="px-4 py-3 rounded-xl inline-flex items-center gap-2 text-sm font-medium"
            style={{
              background: 'rgba(16,185,129,0.08)',
              border: '1px solid rgba(16,185,129,0.30)',
              color: '#6ee7b7',
            }}
            data-testid="streamx-replays-toast"
          >
            <Check className="h-4 w-4" /> {toast}
          </div>
        )}
        {error && (
          <div
            className="p-5 rounded-2xl flex items-start gap-3 text-sm font-medium"
            style={{
              background: 'rgba(255,59,48,0.06)',
              border: '1px solid rgba(255,59,48,0.30)',
              color: '#FFB4AD',
            }}
            data-testid="streamx-replays-error"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="streamx-replays-loading">
            {Array.from({ length: 8 }).map((_, i) => <ShimmerCard key={i} />)}
          </div>
        ) : showEmpty ? (
          <ArchiveEmptyState />
        ) : (
          <>
            <Anim>
              <ArchiveControls
                search={search}
                setSearch={setSearch}
                sports={availableSports}
                activeSport={activeSport}
                setActiveSport={setActiveSport}
                total={filtered.length}
              />
            </Anim>

            {showNoResults ? (
              <NoResultsBlock onClear={() => { setSearch(''); setActiveSport('all'); }} />
            ) : (
              BUCKET_ORDER.map((lbl) => {
                const items = grouped[lbl];
                if (!items || items.length === 0) return null;
                return (
                  <section key={lbl} data-testid={`streamx-replays-bucket-${lbl.toLowerCase().replace(/\s/g, '-')}`}>
                    <Anim>
                      <div className="flex items-end justify-between mb-5">
                        <div>
                          <div className="text-[10px] font-bold tracking-[0.3em] text-white/45 uppercase mb-1.5 inline-flex items-center gap-1.5">
                            <Calendar className="h-3 w-3" /> {lbl}
                          </div>
                          <h2 className="text-2xl font-bold tracking-tight text-white">
                            {items.length} {items.length === 1 ? 'replay' : 'replays'}
                          </h2>
                        </div>
                      </div>
                    </Anim>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                      {items.map((r, i) => (
                        <Anim key={r.id} delay={i * 0.04}>
                          <ReplayCard r={r} onShare={handleShare} onDelete={handleDelete} onRetry={handleRetry} busy={busyId === r.id} />
                        </Anim>
                      ))}
                    </div>
                  </section>
                );
              })
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <section
        className="border-t"
        style={{ background: 'rgba(0,0,0,0.4)', borderColor: 'rgba(255,255,255,0.06)' }}
        data-testid="streamx-replays-footer"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex items-center justify-between gap-3 flex-wrap text-[12px] font-medium text-white/45">
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" />
            {STREAMX_BRAND} · Your matches, archived forever
          </div>
          <div className="flex items-center gap-5">
            <Link to="/streamx/live" className="hover:text-white transition-colors">Live</Link>
            <Link to="/streamx/stories" className="hover:text-white transition-colors">Stories</Link>
            <Link to="/streamx/pricing" className="hover:text-white transition-colors">Pricing</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
